// shared-lib/cabinet-runs/cabinetFrontSpecs.js
//
// The numbers a box cabinet's FRONT is built from, in metres — the carcass,
// the reveal and gaps between fronts, the toe kick, how many doors a width
// takes and how many drawers a height, and where the hardware sits.
//
// They live here, not in the web app's `proceduralSpecs.js` (which
// re-exports them), because the estimate counts the same fronts the renderer
// draws: every pull it bills is a front `cabinetFrontLayout` lays out from
// these numbers (`cabinetHardware.js`). A copy in either place would let the
// drawing and the bill disagree about a door.
//
// No dependencies on purpose: the renderer imports it.

const IN_TO_M = 0.0254;

// Frameless ("European") box construction, the standard the renderer builds:
// a 3/4" carcass, full-overlay 3/4" slab fronts set in a 1/8" reveal with a
// 5/16" gap between fronts, and — on a base cabinet — a 4" toe kick recessed
// 3" behind the carcass front. A standard base is 34.5" high x 24" deep, a
// standard wall cabinet 12" deep.
//
// The DECLARED DEPTH IS MEASURED TO THE DOOR FACE: carcass + door. That is
// what a tape reads on a real 24" base, what the kitchen materializer sets as
// `dimensions.z` (24", under a 25" countertop whose 1" nosing is the counter's
// own), and what a RoomPlan scan of a cabinet run reads. Hardware is NOT part
// of it — a pull stands `frontGap + barDiameter` (~0.67") proud of the door
// face in both the 3D mesh and the elevation, exactly as a real pull stands
// proud of a nominal 24" box. A built bbox of 24.67" against a declared 24"
// is therefore correct, not drift.
//
// The front layout both surfaces draw from these numbers — where every door,
// drawer, rail, apron and filler sits — is `cabinetFrontLayout.js` (pure).
const CABINET = {
  panelThick: 0.019, // 3/4" carcass panel / front slab
  doorReveal: 0.003, // reveal between the front set and the carcass edge
  doorGap: 0.008, // gap between adjacent fronts
  toeKickHeight: 0.1, // 4"
  toeKickSetback: 0.076, // 3" behind the carcass front
  maxDoorWidth: 0.457, // 18" — widest single door in a wide bank
  maxSingleDoorWidth: 0.3048, // 12" — one door up to here
  maxTwoDoorWidth: 1.2192, // 48" — a pair up to here
  maxDrawerHeight: 0.203, // ~8" per drawer in a default stack
  minDrawerCount: 2,
  // The false rail over doors, the drawer-doors working drawer and the
  // sink-front apron are all ONE drawer of the default stack above, so that
  // neighbouring columns / bays line up — `cabinetTopFrontHeight`.
  farmhouseApronDrop: 8.5 * IN_TO_M, // a 10" apron under a 1.5" counter
  fillerMin: 0.005, // narrower remainders are not worth a filler strip
  // A farmhouse apron narrower than its column leaves open band either side;
  // anything under this is float dust, not a panel.
  farmhouseBandMin: 0.25 * IN_TO_M,
  // Framed door styles (shaker / raised-panel / glass-front): rail + stile
  // width, narrower on drawer fronts, never more than a quarter of the front.
  railWidth: 0.0635, // 2.5"
  drawerRailWidth: 0.038, // 1.5"
  // A slim shaker's thin edge, doors and drawers alike: the 13 mm stile and
  // rail on Tasoro's Thin Shaker cut sheet.
  slimRailWidth: 0.013,
  railMaxFrac: 0.25,
  raisedPanelInset: 0.003, // raised field sits this far inside the frame opening
  // The under-cabinet LED strip's channel under a flagged upper
  // (`cabinetLightStrip`): 12 mm across the run, 3 mm tall. Where it sits
  // along and across the underside comes from `@shared-lib/under-cabinet-
  // lighting` (the same insets the strip LIGHT is placed with).
  lightStripDepth: 0.012,
  lightStripHeight: 0.003,
  // An OPEN SHELF (the box with no doors): the clear height its compartments
  // aim for. The shelf COUNT derives from it — `cabinetOpenShelfCount` — so
  // the 3D box and the elevation always carry the same number of shelves.
  openShelfSpacing: 12 * IN_TO_M,
  // The INSIDE of a closed box (`cabinetCompartments.js`), which shows when a
  // door swings open or a drawer slides out. Shelves: one at mid-height in a
  // base, two in a wall cabinet, one every `openShelfSpacing` in a tall unit
  // (a lower whose body passes `tallBodyMin`) — never so many that a level's
  // clear height drops below `minShelfLevel`.
  baseShelfCount: 1,
  upperShelfCount: 2,
  tallBodyMin: 48 * IN_TO_M,
  minShelfLevel: 8 * IN_TO_M,
  // A drawer box: 1/2" walls, 1/2" slide clearance each side, its top 1"
  // below the front's, 1" of air between it and the carcass back, and it
  // slides out this fraction of its own depth.
  drawerBoxThick: 0.5 * IN_TO_M,
  drawerSideClearance: 0.5 * IN_TO_M,
  drawerBoxHeadroom: 1 * IN_TO_M,
  drawerBoxBackGap: 1 * IN_TO_M,
  drawerOpenFrac: 0.75,
  // A door swings this far about its hinge edge (concealed euro hinges open
  // 100–110°).
  doorOpenAngle: (100 * Math.PI) / 180,
  // A ROLL-OUT tray behind a base cabinet's doors (`cabinetRollOutSlots`,
  // `rollOuts.js`): a drawer box 4" tall and at most 21" deep (Venus's RD
  // tray), 1" narrower each side than the opening for its slides and the
  // door hinges, its walls the drawer box's stock. Trays stand at least 3"
  // apart (clear air over one tray's sides to the next one's bottom), and a
  // full-extension slide carries a tray out this fraction of its depth. A box
  // under 12" wide or 15" deep (the shallowest common tray) takes none.
  rollOutTrayHeight: 4 * IN_TO_M,
  rollOutSideClearance: 1 * IN_TO_M,
  rollOutMaxDepth: 21 * IN_TO_M,
  rollOutMinGap: 3 * IN_TO_M,
  rollOutOpenFrac: 0.9,
  rollOutMinWidth: 12 * IN_TO_M,
  rollOutMinDepth: 15 * IN_TO_M,
};

const CABINET_HARDWARE = {
  barLength: 0.1, // 4" bar pull
  barDiameter: 0.012, // ~1/2" tube
  knobRadius: 0.0165, // ~1.3" knob
  edgeInset: 0.03, // pull centre from the door's free edge
  frontGap: 0.005, // standoff between the door face and the hardware
  barBottomOffset: 0.03, // handleAtBottom: bar bottom this far above the door bottom
  knobBottomOffset: 0.05, // handleAtBottom: knob bottom this far above the door bottom
  cupLength: 0.076, // ~3" cup pull, clamped to 60% of the drawer width
  cupLengthFrac: 0.6,
  cupHeight: 0.026, // ~1" tall, clamped to half the drawer height
  cupHeightFrac: 0.5,
  cupProjection: 0.019, // ~3/4" forward bulge
};

/**
 * A floating shelf's board (Venus's flat shelf, catalog p.16: 2" thick, 12"
 * deep) and the spacing the boards run at, up from the uppers' bottom line —
 * the open shelf's (`floatingShelves.js`, group 3b).
 */
const FLOATING_SHELF = Object.freeze({
  thicknessM: 2 * IN_TO_M,
  depthM: 12 * IN_TO_M,
  spacingM: CABINET.openShelfSpacing,
});

/**
 * How many boards a floating-shelf bay of this height holds: one on its
 * bottom, then one every `spacingM` up while a board still fits under its
 * top. The drawing lays out this many and the estimate bills this many.
 */
function floatingShelfCount(heightM) {
  const { thicknessM, spacingM } = FLOATING_SHELF;
  if (!(heightM >= thicknessM)) return 0;
  return Math.floor((heightM - thicknessM) / spacingM + 1e-9) + 1;
}

module.exports = {
  CABINET,
  CABINET_HARDWARE,
  FLOATING_SHELF,
  floatingShelfCount,
};
